// src/pages/personal/analytics/FeedbackSection/components/WordCloud/WordCloud.tsx
import React, { useMemo, useState } from 'react';
import { COLORS } from '@/theme';
import { formatNumber } from '@/utils/Formatters';
import { useTranslation } from 'react-i18next';
import { useResponsive } from '@/hooks/useResponsive';
import styles from '../../FeedbackSection.module.css';

interface WordData {
  text: string;
  value: number;
}

interface WordCloudProps {
  data: WordData[];
}

export const WordCloud: React.FC<WordCloudProps> = ({ data }) => {
  const { i18n } = useTranslation();
  const { isMobile } = useResponsive();
  const [hoveredWord, setHoveredWord] = useState<string | null>(null);
  
  // Находим максимальное и минимальное значение для масштабирования
  const maxValue = useMemo(() => Math.max(...data.map(item => item.value)), [data]);
  const minValue = useMemo(() => Math.min(...data.map(item => item.value)), [data]);
  
  // Функция для масштабирования размера шрифта
  const getWordSize = (value: number) => {
    const minSize = isMobile ? 10 : 12;
    const maxSize = isMobile ? 28 : 40;
    
    // Если все значения одинаковые, возвращаем средний размер
    if (maxValue === minValue) {
      return (minSize + maxSize) / 2;
    }
    
    // Масштабирование значения от minSize до maxSize
    return minSize + ((value - minValue) / (maxValue - minValue)) * (maxSize - minSize);
  };
  
  // Функция для получения цвета из палитры темы на основе значения
  const getWordColor = (value: number) => {
    // Палитра из 5 оттенков зеленого используя цвета темы
    const colors = [
      COLORS.primary,                      // Основной зеленый
      COLORS.clientChartColors.loyal,      // Другой оттенок
      COLORS.clientChartColors.regular,    // Еще оттенок
      COLORS.clientChartColors.rare,       // Еще оттенок
      COLORS.clientChartColors.new         // Еще оттенок
    ];
    
    // Выбираем цвет в зависимости от значения
    const index = Math.floor((value - minValue) / (maxValue - minValue + 0.001) * colors.length);
    return colors[Math.min(index, colors.length - 1)];
  };
  
  const generateRandomPosition = () => {
    // Генерируем случайные позиции, но с учетом расстояния от края
    return {
      left: Math.random() * 70 + 15, // от 15% до 85%
      top: Math.random() * 70 + 15,  // от 15% до 85%
    };
  };
  
  // Генерируем и запоминаем позиции для каждого слова
  const wordPositions = useMemo(() => {
    return data.map(() => generateRandomPosition());
  }, [data]);
  
  return (
    <div className={styles.wordCloudContainer}>
      {data.map((word, index) => {
        const size = getWordSize(word.value);
        const color = getWordColor(word.value);
        const position = wordPositions[index];
        
        // Стили для слова
        const wordStyle = {
          left: `${position.left}%`,
          top: `${position.top}%`,
          fontSize: `${size}px`,
          color: color,
          fontWeight: size > 25 ? 'bold' : 'normal',
          opacity: hoveredWord === word.text ? 1 : 0.9,
          zIndex: Math.floor(word.value),
          textShadow: hoveredWord === word.text ? '2px 2px 4px rgba(0,0,0,0.2)' : '1px 1px 2px rgba(0,0,0,0.1)'
        };
        
        return (
          <div
            key={index}
            className={styles.wordItem}
            style={wordStyle}
            onMouseEnter={() => setHoveredWord(word.text)}
            onMouseLeave={() => setHoveredWord(null)}
          >
            {word.text}
            
            {/* Подсказка с количеством упоминаний */}
            {hoveredWord === word.text && (
              <div className={styles.wordTooltip}>
                {formatNumber(word.value, 0, true, i18n.language)} упоминаний
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};